import React, { useMemo } from 'react';
import { FileText } from 'lucide-react';

interface TemplateElement {
  id: string;
  type: 'text' | 'image' | 'divider' | 'signature';
  content?: string;
  imageUrl?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  styles: {
    fontSize: number;
    fontFamily: string;
    fontWeight: 'normal' | 'bold';
    fontStyle: 'normal' | 'italic';
    textDecoration: 'none' | 'underline';
    textAlign: 'left' | 'center' | 'right';
    color: string;
    backgroundColor?: string;
    padding: number;
    borderRadius: number;
    border?: string;
    lineHeight?: number;
  };
}

interface TemplateLayout {
  id: string;
  name: string;
  pageSize: 'letter' | 'a4';
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  elements: TemplateElement[];
}

interface TemplatePreviewThumbnailProps {
  template: TemplateLayout;
  size?: 'small' | 'medium' | 'large';
  interactive?: boolean;
  className?: string;
}

// Sample data for preview - matches common template variables
const getSamplePreviewData = (): Record<string, string> => ({
  // Borrower Information
  '{{borrowerFirstName}}': 'John',
  '{{borrowerLastName}}': 'Smith',
  '{{borrowerEmail}}': 'john.smith@example.com',
  '{{borrowerPhone}}': '(555) 123-4567',

  // Loan Information
  '{{loanAmount}}': '$450,000',
  '{{purchasePrice}}': '$500,000',
  '{{downPaymentAmount}}': '$50,000',
  '{{downPaymentPercent}}': '10%',
  '{{loanType}}': 'CONVENTIONAL',

  // Property Information
  '{{propertyStreet}}': '123 Main Street',
  '{{propertyCity}}': 'San Francisco',
  '{{propertyState}}': 'CA',
  '{{propertyZipCode}}': '94102',
  '{{propertyType}}': 'Single Family',
  '{{propertyOccupancy}}': 'Primary Residence',

  // Dates
  '{{expirationDate}}': '12/31/2025',
  '{{currentDate}}': new Date().toLocaleDateString(),
  '{{dateOfPreApproval}}': new Date().toLocaleDateString(),

  // Loan Officer Information
  '{{loanOfficerName}}': 'Jane Doe',
  '{{loanOfficerFirstName}}': 'Jane',
  '{{loanOfficerLastName}}': 'Doe',
  '{{loanOfficerTitle}}': 'Senior Loan Officer',
  '{{loanOfficerNMLS}}': 'NMLS# 123456',
  '{{loanOfficerPhone}}': '(555) 987-6543',
  '{{loanOfficerMobile}}': '(555) 987-6543',
  '{{loanOfficerEmail}}': 'jane.doe@lendwise.com',
  '{{loanOfficerSignature}}': 'Jane Doe',

  // Company Information
  '{{companyName}}': 'Lendwise Mortgage',
  '{{companyNMLS}}': 'NMLS# 987654',
  '{{companyPhone}}': '(555) 555-5555',
  '{{companyEmail}}': 'info@lendwise.com',
  '{{companyWebsite}}': 'www.lendwise.com',
  '{{companyAddress}}': '456 Business Blvd, San Francisco, CA 94105',
  '{{companyStreet}}': '456 Business Blvd',
  '{{companyCity}}': 'San Francisco',
  '{{companyState}}': 'CA',
  '{{companyZip}}': '94105',
  '{{loanOfficerWebsite}}': 'www.lendwise.com',

  // Additional
  '{{ltv}}': '90%',
  '{{preApprovalDisclaimer}}': 'Subject to verification',
  '{{equalHousingLender}}': 'Equal Housing Lender'
});

// Replace template variables with sample data
const replaceVariables = (text: string): string => {
  if (!text) {
return '';
}

  const variables = getSamplePreviewData();
  let result = text;

  Object.entries(variables).forEach(([variable, value]) => {
    result = result.replace(new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
  });

  return result;
};

export const TemplatePreviewThumbnail: React.FC<TemplatePreviewThumbnailProps> = React.memo(({
  template,
  size = 'medium',
  interactive = false,
  className = ''
}) => {
  // Size variants - maintains letter aspect ratio (1:1.294)
  const PREVIEW_SIZES = {
    small: { width: 160, height: 207 },   // Mobile
    medium: { width: 240, height: 310 },  // Desktop (default)
    large: { width: 280, height: 363 }    // Immersive
  } as const;

  // Get preview dimensions
  const previewDimensions = PREVIEW_SIZES[size];

  // Calculate page dimensions at 72 DPI
  const pageWidth = template.pageSize === 'letter' ? 612 : 595;
  const pageHeight = template.pageSize === 'letter' ? 792 : 842;

  // Calculate scale to fit preview width perfectly
  const scale = previewDimensions.width / pageWidth;

  // Render a single element
  const renderElement = (element: TemplateElement) => {
    const elementStyles: React.CSSProperties = {
      position: 'absolute',
      left: `${element.x}px`,
      top: `${element.y}px`,
      width: `${element.width}px`,
      height: element.type === 'text' ? 'auto' : `${element.height}px`,
      fontSize: `${element.styles.fontSize}px`,
      fontFamily: element.styles.fontFamily,
      fontWeight: element.styles.fontWeight,
      fontStyle: element.styles.fontStyle,
      textDecoration: element.styles.textDecoration,
      textAlign: element.styles.textAlign,
      color: element.styles.color,
      backgroundColor: element.styles.backgroundColor,
      padding: `${element.styles.padding}px`,
      borderRadius: `${element.styles.borderRadius}px`,
      border: element.styles.border,
      lineHeight: element.styles.lineHeight,
      overflow: 'hidden',
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word'
    };

    switch (element.type) {
      case 'text':
        return (
          <div key={element.id} style={elementStyles}>
            {replaceVariables(element.content || '')}
          </div>
        );

      case 'image':
        if (!element.imageUrl && !element.content) {
return null;
}
        return (
          <img
            key={element.id}
            src={element.imageUrl || element.content || ''}
            alt="Template"
            style={elementStyles}
            onError={(e) => {
              // Hide broken images
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        );

      case 'divider':
        return (
          <div
            key={element.id}
            style={{
              ...elementStyles,
              borderBottom: element.styles.border || `1px solid ${element.styles.color}`,
              height: '1px'
            }}
          />
        );

      case 'signature':
        return (
          <div
            key={element.id}
            style={{
              ...elementStyles,
              borderBottom: '1px solid #888',
              fontSize: '10px',
              color: '#888'
            }}
          >
            [Signature]
          </div>
        );

      default:
        return null;
    }
  };

  // Memoize the preview content
  const previewContent = useMemo(() => {
    // Handle empty or invalid templates
    if (!template?.elements || template.elements.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <FileText className="w-8 h-8 mb-1" />
          <p className="text-xs">Empty Template</p>
        </div>
      );
    }

    return (
      <div
        className="relative overflow-hidden"
        style={{
          width: `${previewDimensions.width}px`,
          height: `${previewDimensions.height}px`
        }}
      >
        <div
          style={{
            width: `${pageWidth}px`,
            height: `${pageHeight}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            backgroundColor: 'white',
            position: 'absolute',
            top: 0,
            left: 0,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {template.elements.map(renderElement)}
        </div>
      </div>
    );
  }, [template, previewDimensions, scale]);

  return (
    <div
      className={`bg-gray-50 rounded-lg overflow-hidden ${interactive ? 'transition-transform duration-200 ease-out hover:scale-105' : ''} ${className}`}
      style={{
        width: `${previewDimensions.width}px`,
        height: `${previewDimensions.height}px`,
        position: 'relative'
      }}
    >
      {previewContent}
    </div>
  );
});

TemplatePreviewThumbnail.displayName = 'TemplatePreviewThumbnail';
